"use client";

import Link from "next/link";
import { memo } from "react";
import { motion } from "framer-motion";
import { Pause, Play, Loader2, BellOff } from "lucide-react";

import type { AlertDoc } from "@/lib/api-client";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from "./ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { MoreHorizontalIcon, TrashIcon } from "./icons";
import { cn } from "@/lib/utils";

/**
 * Per-status visual treatment. Mirrors the "live" dot on the upload-success
 * card in components/tool-displays/alerts/display.tsx so the language stays
 * consistent across the app.
 */
function statusDotClass(status: string | undefined): string {
  switch (status) {
    case "active":
      return "bg-emerald-500 animate-pulse";
    case "testing":
      return "bg-slate-400 animate-pulse";
    case "paused":
      return "bg-amber-500";
    case "errored":
    case "test_failed":
      return "bg-red-500";
    default:
      return "bg-muted-foreground/40";
  }
}

/**
 * Statuses the owner can recover from via Resume. `test_failed` is usually a
 * first-run timeout artifact (the run itself often succeeded); the backend
 * resume endpoint re-arms the schedule from any of these.
 */
export function isResumableStatus(status: string | undefined): boolean {
  return status === "paused" || status === "test_failed" || status === "errored";
}

type Props = {
  alert: AlertDoc;
  isActive: boolean;
  isBusy?: boolean;
  /**
   * Owner rows (default) get Pause/Resume + Delete. Subscribed rows
   * (`isOwner={false}`) are read-only and get an Unsubscribe action only —
   * pause/delete are owner-only on the backend and would 403/404.
   */
  isOwner?: boolean;
  /**
   * True only for a row that was JUST added to the list (e.g. the user just
   * subscribed and we optimistically inserted it). Plays a one-shot pop +
   * shake + green flash on mount. Existing rows pass false → render static,
   * so nothing else in the sidebar ever animates or shifts.
   */
  justAdded?: boolean;
  onToggleStatus: (alert: AlertDoc) => void;
  onDelete: (alert: AlertDoc) => void;
  onUnsubscribe?: (alert: AlertDoc) => void;
  setOpenMobile: (open: boolean) => void;
};

function PureAlertItem({
  alert,
  isActive,
  isBusy = false,
  isOwner = true,
  justAdded = false,
  onToggleStatus,
  onDelete,
  onUnsubscribe,
  setOpenMobile,
}: Props) {
  const status = alert.status;
  // "Resume" shows for every recoverable non-active state (paused,
  // test_failed, errored) so no alert is ever stuck with only Delete.
  const showResume = isResumableStatus(status);
  const canTogglePause = status === "active" || showResume;

  // One-shot entrance for a freshly added row. Non-new rows get
  // `initial: false` (and no `animate`) so they render static — guaranteeing
  // the rest of the list never moves, flashes, or re-animates.
  const motionProps = justAdded
    ? {
        initial: { opacity: 0, scale: 0.9 },
        animate: {
          opacity: 1,
          scale: 1,
          x: [0, -5, 5, -4, 4, -2, 2, 0],
          backgroundColor: [
            "rgba(16,185,129,0.22)",
            "rgba(16,185,129,0.22)",
            "rgba(16,185,129,0)",
          ],
        },
        transition: {
          duration: 0.6,
          ease: "easeOut" as const,
          backgroundColor: { duration: 1.0, ease: "easeOut" as const },
        },
      }
    : { initial: false as const };

  return (
    <motion.div {...motionProps} className="rounded-md">
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive}>
        <Link
          href={`/alerts/${alert._id}`}
          onClick={() => setOpenMobile(false)}
          className="flex items-center gap-2"
          title={alert.name}
        >
          <span
            className={cn(
              "inline-block size-2 shrink-0 rounded-full",
              statusDotClass(status)
            )}
            aria-hidden
          />
          {alert.kind === "signal" && (
            <span
              className="shrink-0 rounded border border-emerald-500/30 bg-emerald-500/10 px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide text-emerald-400"
              title="Trading signal"
            >
              Signal
            </span>
          )}
          <span
            className={cn(
              "truncate min-w-0",
              isActive
                ? "text-[hsl(var(--sidebar-session-title-active))]"
                : "text-[hsl(var(--sidebar-session-title))]"
            )}
          >
            {alert.name || "Untitled alert"}
          </span>
        </Link>
      </SidebarMenuButton>

      <DropdownMenu modal={true}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground mr-0.5"
            showOnHover={!isActive}
          >
            <MoreHorizontalIcon />
            <span className="sr-only">More</span>
          </SidebarMenuAction>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="bottom" align="end">
          {isOwner ? (
            <>
              <DropdownMenuItem
                className="cursor-pointer"
                disabled={!canTogglePause || isBusy}
                onClick={() => onToggleStatus(alert)}
              >
                {isBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : showResume ? (
                  <Play className="h-4 w-4" />
                ) : (
                  <Pause className="h-4 w-4" />
                )}
                <span>{showResume ? "Resume" : "Pause"}</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                className="cursor-pointer text-destructive focus:bg-destructive/15 focus:text-destructive dark:text-red-500"
                onSelect={() => onDelete(alert)}
              >
                <TrashIcon />
                <span>Delete</span>
              </DropdownMenuItem>
            </>
          ) : (
            <DropdownMenuItem
              className="cursor-pointer"
              disabled={isBusy}
              onSelect={() => onUnsubscribe?.(alert)}
            >
              {isBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <BellOff className="h-4 w-4" />
              )}
              <span>Unsubscribe</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
    </motion.div>
  );
}

export const AlertItem = memo(PureAlertItem, (prev, next) => {
  if (prev.isActive !== next.isActive) return false;
  if (prev.isBusy !== next.isBusy) return false;
  if (prev.isOwner !== next.isOwner) return false;
  if (prev.alert._id !== next.alert._id) return false;
  if (prev.alert.name !== next.alert.name) return false;
  if (prev.alert.status !== next.alert.status) return false;
  if (prev.alert.kind !== next.alert.kind) return false;
  return true;
});
